import { useEffect, useRef, useCallback, useState } from 'react';
import {
  useGetCampusBuildingsQuery,
  useGetCampusStatusesQuery
} from '../api/homeApi';
import { POLLING_CONFIG } from '../utils/mapConfig';

export const useMapPolling = (mapState) => {
  const pollIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  // Stable ref to mapState so effects don't re-run when mapState changes identity
  const mapStateRef = useRef(mapState);
  mapStateRef.current = mapState;

  const [isPolling, setIsPolling] = useState(false);
  const [pollError, setPollError] = useState(null);

  // Fetch buildings once on initialization (no polling)
  const { data: buildingsData, isLoading: buildingsLoading } =
    useGetCampusBuildingsQuery();

  // Fetch statuses - manage polling manually
  const {
    data: statusesData,
    isLoading: statusesLoading,
    refetch: refetchStatuses
  } = useGetCampusStatusesQuery(undefined, {
    refetchOnMountArgChange: false
  });

  /**
   * Initialize buildings on first load
   */
  useEffect(() => {
    if (buildingsData && !buildingsLoading) {
      try {
        const buildingArray = buildingsData.hosts || buildingsData;
        mapStateRef.current.initializeBuildings(buildingArray);
        mapStateRef.current.setLoadingState(false);
        console.log(`Initialized ${buildingArray.length} buildings`);
      } catch (err) {
        mapStateRef.current.setErrorMessage('Failed to load buildings');
        console.error('Building initialization error:', err);
      }
    }
  }, [buildingsData, buildingsLoading]);

  /**
   * Handle status update from polling
   */
  useEffect(() => {
    if (statusesData && !statusesLoading) {
      try {
        const statusArray = statusesData.hosts || statusesData;
        mapStateRef.current.updateStatuses(statusArray);
        setPollError(null);
      } catch (err) {
        setPollError('Status update failed');
        console.error('Status update error:', err);
      }
    }
  }, [statusesData, statusesLoading]);

  /**
   * Perform a single status refresh (safe version)
   */
  const refreshStatuses = useCallback(async () => {
    try {
      if (refetchStatuses) {
        await refetchStatuses();
      }
    } catch (err) {
      console.error('Refresh error:', err);
      setPollError('Failed to refresh statuses');
    }
  }, [refetchStatuses]);

  /**
   * Start polling for status updates
   */
  const startPolling = useCallback(() => {
    if (isPollingRef.current) return;

    isPollingRef.current = true;
    setIsPolling(true);

    // First immediate refresh
    refreshStatuses();

    // Then set up interval
    pollIntervalRef.current = setInterval(() => {
      if (isPollingRef.current) {
        refreshStatuses();
      }
    }, POLLING_CONFIG.defaultInterval);

    console.log(
      `Campus map polling started (${POLLING_CONFIG.defaultInterval / 1000}s interval)`
    );
  }, [refreshStatuses]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    isPollingRef.current = false;
    setIsPolling(false);
    console.log('Campus map polling stopped');
  }, []);

  /**
   * Manual refresh trigger
   */
  const refresh = useCallback(async () => {
    mapStateRef.current.setLoadingState(true);
    try {
      await refreshStatuses();
    } finally {
      mapStateRef.current.setLoadingState(false);
    }
  }, [refreshStatuses]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isPolling,
    pollError,
    startPolling,
    stopPolling,
    refresh,
    refreshStatuses
  };
};
